import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const IndiaTourismVision = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const highlights = [
    {
      icon: 'fas fa-laptop',
      title: 'Inspired by Digital India',
      description: 'Building digital-first solutions for sustainable tourism growth'
    },
    {
      icon: 'fas fa-map-location-dot',
      title: 'Promoting Local Tourism',
      description: 'Empowering local communities and offbeat destinations'
    },
    {
      icon: 'fas fa-people-carry',
      title: 'Supporting Rural & Offbeat',
      description: 'Celebrating hidden gems and authentic experiences'
    },
    {
      icon: 'fas fa-rocket',
      title: 'Startup Ecosystem Driven',
      description: 'Innovation-focused travel-tech platform for modern travelers'
    },
    {
      icon: 'fas fa-handshake',
      title: 'Empowering Local Hosts',
      description: 'Supporting homestay owners, campsite operators, and local businesses'
    },
    {
      icon: 'fas fa-sparkles',
      title: 'Travel-Tech Innovation',
      description: 'Leveraging technology to simplify travel discovery and booking'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className={`india-tourism-vision-section ${isVisible ? 'visible' : ''}`}
    >
      <div className="vision-container">
        {/* Left Visual Area */}
        <div className="vision-visual-area">
          <div className="vision-collage">
            <div className="collage-item leader-card leader-1">
              <img 
                src="/images/modi-r.png" 
                alt="PM Vision for Tourism"
                loading="lazy"
              />
              <p className="leader-label">PM's Tourism Vision</p>
            </div>

            <div className="collage-item leader-card leader-2">
              <img 
                src="/images/yogi.png" 
                alt="UP Tourism Development"
                loading="lazy"
              />
              <p className="leader-label">UP Tourism Growth</p>
            </div>

            <div className="collage-item logo-card logo-1">
              <img 
                src="/images/standup-india.jpg" 
                alt="Startup India Logo"
                loading="lazy"
              />
            </div>

            <div className="collage-item logo-card logo-2">
              <img 
                src="/images/incredible_india.jpg" 
                alt="Incredible India Tourism Logo"
                loading="lazy"
              />
            </div>

            <div className="vision-watermark">
              <i className="fas fa-map"></i>
            </div>
          </div>

          <p className="vision-attribution">
            <i className="fas fa-info-circle"></i>
            Inspired by National Tourism & Startup Vision
          </p>
        </div>

        {/* Right Content Area */}
        <div className="vision-content-area">
          <div className="vision-header">
            <h2>Empowering India's Tourism Future</h2>
            <p className="vision-subtitle">
              RedGoTrip is committed to empowering local hosts, hidden destinations, offbeat tourism, camping, homestays, trekking, rural experiences, and regional travel businesses through a digital-first platform.
            </p>
          </div>

          {/* Highlight Cards */}
          <div className="highlights-grid">
            {highlights.map((highlight, index) => (
              <div key={index} className="highlight-card">
                <div className="highlight-icon">
                  <i className={highlight.icon}></i>
                </div>
                <h4>{highlight.title}</h4>
                <p>{highlight.description}</p>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="vision-cta-buttons">
            <Link to="/packages" className="btn btn-primary">
              Explore Experiences
              <i className="fas fa-arrow-right"></i>
            </Link>
            <Link to="/become-host" className="btn btn-secondary">
              Become a Host
              <i className="fas fa-arrow-right"></i>
            </Link>
          </div>

          {/* Disclaimer */}
          <div className="vision-disclaimer">
            <p>
              <strong>Disclaimer:</strong> RedGoTrip is an independent travel-tech platform inspired by national tourism and startup initiatives. Logos and images are used only for visual representation and alignment with public initiatives. We are not officially endorsed by any government organization.
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        .india-tourism-vision-section {
          background: linear-gradient(135deg, rgba(255, 153, 0, 0.05) 0%, rgba(19, 112, 43, 0.05) 100%);
          padding: 80px 20px;
          position: relative;
          overflow: hidden;
        }

        .india-tourism-vision-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 50%, rgba(255, 153, 0, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(19, 112, 43, 0.03) 0%, transparent 50%);
          pointer-events: none;
        }

        .vision-container {
          max-width: 1400px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 60px;
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .vision-visual-area {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .vision-collage {
          position: relative;
          height: 550px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          gap: 16px;
        }

        .collage-item {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeInScale 0.8s ease-out backwards;
        }

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .india-tourism-vision-section.visible .collage-item {
          animation-play-state: running;
        }

        .collage-item:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.15);
        }

        .leader-card {
          grid-column: span 1;
          grid-row: span 1;
          background: linear-gradient(135deg, #fff5e6 0%, #f0f9ff 100%);
        }

        .leader-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .leader-label {
          position: absolute;
          bottom: 12px;
          left: 12px;
          right: 12px;
          background: rgba(255, 153, 0, 0.95);
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
          margin: 0;
          text-align: center;
        }

        .logo-card {
          background: white;
          padding: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-card img {
          max-width: 90%;
          max-height: 90%;
          object-fit: contain;
        }

        .vision-watermark {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 120px;
          color: rgba(0, 0, 0, 0.02);
          pointer-events: none;
          z-index: 0;
        }

        .vision-attribution {
          font-size: 0.9rem;
          color: #666;
          text-align: center;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .vision-attribution i {
          color: #FF9900;
        }

        .vision-content-area {
          display: flex;
          flex-direction: column;
          gap: 32px;
          animation: fadeInRight 0.8s ease-out;
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .india-tourism-vision-section.visible .vision-content-area {
          animation: fadeInRight 0.8s ease-out forwards;
        }

        .vision-header h2 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 16px 0;
          line-height: 1.3;
          background: linear-gradient(135deg, #1a1a1a 0%, #4361EE 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .vision-subtitle {
          font-size: 1.05rem;
          color: #555;
          line-height: 1.8;
          margin: 0;
        }

        .highlights-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .highlight-card {
          background: white;
          border-left: 4px solid;
          border-left-color: #FF9900;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
        }

        .highlight-card:nth-child(2) {
          border-left-color: #137030;
        }

        .highlight-card:nth-child(3) {
          border-left-color: #4361EE;
        }

        .highlight-card:nth-child(4) {
          border-left-color: #FF9900;
        }

        .highlight-card:nth-child(5) {
          border-left-color: #137030;
        }

        .highlight-card:nth-child(6) {
          border-left-color: #4361EE;
        }

        .highlight-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
        }

        .highlight-icon {
          font-size: 1.8rem;
          margin-bottom: 12px;
          background: linear-gradient(135deg, #FF9900, #137030);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .highlight-card h4 {
          font-size: 1rem;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0 0 8px 0;
        }

        .highlight-card p {
          font-size: 0.9rem;
          color: #666;
          margin: 0;
          line-height: 1.5;
        }

        .vision-cta-buttons {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 28px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.3s ease;
          outline: none;
        }

        .btn-primary {
          background: linear-gradient(135deg, #FF9900 0%, #E68A00 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(255, 153, 0, 0.3);
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(255, 153, 0, 0.4);
        }

        .btn-secondary {
          background: white;
          color: #137030;
          border: 2px solid #137030;
          box-shadow: 0 2px 8px rgba(19, 112, 48, 0.1);
        }

        .btn-secondary:hover {
          background: #137030;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(19, 112, 48, 0.3);
        }

        .btn i {
          font-size: 0.9rem;
        }

        .vision-disclaimer {
          background: linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(19, 112, 48, 0.1) 100%);
          border: 1px solid rgba(255, 153, 0, 0.3);
          border-radius: 8px;
          padding: 16px;
        }

        .vision-disclaimer p {
          margin: 0;
          font-size: 0.9rem;
          color: #555;
          line-height: 1.6;
        }

        .vision-disclaimer strong {
          color: #1a1a1a;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .vision-container {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .vision-header h2 {
            font-size: 2rem;
          }

          .highlights-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .india-tourism-vision-section {
            padding: 60px 20px;
          }

          .vision-collage {
            height: 400px;
          }

          .vision-header h2 {
            font-size: 1.8rem;
          }

          .vision-subtitle {
            font-size: 1rem;
          }

          .highlights-grid {
            grid-template-columns: 1fr;
          }

          .vision-cta-buttons {
            flex-direction: column;
          }

          .btn {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .india-tourism-vision-section {
            padding: 50px 16px;
          }

          .vision-container {
            gap: 30px;
          }

          .vision-collage {
            height: 300px;
            grid-template-columns: 1fr 1fr;
          }

          .vision-header h2 {
            font-size: 1.5rem;
          }

          .vision-subtitle {
            font-size: 0.95rem;
          }

          .highlight-card {
            padding: 16px;
          }

          .highlight-icon {
            font-size: 1.5rem;
          }

          .highlight-card h4 {
            font-size: 0.95rem;
          }

          .highlight-card p {
            font-size: 0.85rem;
          }

          .btn {
            padding: 12px 20px;
            font-size: 0.95rem;
          }
        }
      `}</style>
    </section>
  );
};

export default IndiaTourismVision;
